import { ComponentType } from 'react';

import { Button } from '@components/button';
import { ExpandableSearch } from '@components/expandable-search';
import { IconProps } from '@components/icons';
import { IconAttachment } from '@components/icons/attachment';
import { IconAttachmentMultiple } from '@components/icons/attachment-multiple';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconDate } from '@components/icons/date';
import { IconDecimalFloat } from '@components/icons/decimal-float';
import { IconEmail } from '@components/icons/email';
import { IconPerson } from '@components/icons/person';
import { IconPhone } from '@components/icons/phone';
import { IconSelectMultiple } from '@components/icons/select-multiple';
import { IconSelectSingle } from '@components/icons/select-single';
import { IconTextFormatNumber } from '@components/icons/text-format-number';
import { IconTextLong } from '@components/icons/text-long';
import { IconTextShort } from '@components/icons/text-short';
import { Stack } from '@components/stack';
import { getScopedTranslate, useScopedTranslate } from '@locale/translate';

import { AttributeFieldType } from 'api/attributes/requests';

type AttributeIconProps = IconProps & {
  type: AttributeFieldType;
};

export const AttributeIcon = ({ type, ...rest }: AttributeIconProps) => {
  const Icon = attributeIcons[type];

  if (!Icon) {
    console.error(`Unable to find icon for attribute type: ${type}`);
    return null;
  }

  return <Icon {...rest} />;
};

const attributeIcons: Record<AttributeFieldType, ComponentType<IconProps>> = {
  date: IconDate,
  text: IconTextShort,
  paragraph: IconTextLong,
  user: IconPerson,
  phone_number: IconPhone,
  email: IconEmail,
  boolean: IconCheckmark,
  single_select: IconSelectSingle,
  multi_select: IconSelectMultiple,
  file: IconAttachment,
  file_multi: IconAttachmentMultiple,
  number: IconTextFormatNumber,
  decimal: IconDecimalFloat,
};

export const AttributeActions = ({
  setSearchStr,
  searchStr,
  onAddAttribute,
  isTravellerAttribute,
}: {
  setSearchStr: (searchStr: string) => void;
  searchStr: string;
  onAddAttribute: () => void;
  isTravellerAttribute?: boolean;
}) => {
  const { t } = useScopedTranslate(locale);
  return (
    <Stack space={2} orientation="horizontal">
      <ExpandableSearch onChange={setSearchStr} defaultValue={searchStr} />
      <Button
        variant="primary"
        text={t('addAttribute')}
        iconLeft={isTravellerAttribute ? <IconAttributeTraveller /> : <IconAttributeJourney />}
        onClick={onAddAttribute}
      />
    </Stack>
  );
};

const locale = {
  en: {
    addAttribute: 'Create field',
    date: 'Select - Date',
    text: 'Text - Short',
    paragraph: 'Text - Long',
    user: 'Select - User',
    phone_number: 'Telephone',
    email: 'Email',
    single_select: 'Select - Single',
    multi_select: 'Select - Multi',
    boolean: 'On / off',
    file: 'File',
    number: 'Number',
    decimal: 'Number (Decimal)',
  },
};

const { t: mapTranslate } = getScopedTranslate(locale);

export const attributeTextMap: Record<AttributeFieldType, string> = {
  date: mapTranslate('date'),
  text: mapTranslate('text'),
  paragraph: mapTranslate('paragraph'),
  user: mapTranslate('user'),
  phone_number: mapTranslate('phone_number'),
  email: mapTranslate('email'),
  boolean: mapTranslate('boolean'),
  single_select: mapTranslate('single_select'),
  multi_select: mapTranslate('multi_select'),
  file: mapTranslate('file'),
  file_multi: mapTranslate('file'),
  number: mapTranslate('number'),
  decimal: mapTranslate('decimal'),
};
